/**
 * Drag-and-drop + bulk upload, with a progress bar and an error message per
 * file rather than one for the batch.
 *
 * The queue itself lives in `useAssetUploadQueue`; this file is the surface.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Button } from '@/components/common';
import { cn } from '@/utils';
import { formatBytes, type AssetConfig, type MediaAsset } from '@/services/assets';
import { useAssetUploadQueue, type UploadItem } from '@/hooks/useAssetUploadQueue';

interface UploadDropzoneProps {
  /** Folder the batch lands in. */
  folder?: string | null;
  config?: AssetConfig | null;
  onUploaded?: (asset: MediaAsset) => void;
  onBatchComplete?: (assets: MediaAsset[]) => void;
  /** Told whenever the queue starts/stops, so a dialog can block its close. */
  onBusyChange?: (busy: boolean) => void;
  className?: string;
  compact?: boolean;
}

export function UploadDropzone({
  folder = null,
  config,
  onUploaded,
  onBatchComplete,
  onBusyChange,
  className,
  compact = false,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // A drag over a child element fires dragleave on the parent; count the
  // enter/leave pairs instead of toggling a boolean, or the highlight flickers.
  const dragDepth = useRef(0);

  const queue = useAssetUploadQueue({
    folder,
    config,
    onUploaded,
    onBatchComplete,
  });

  // In an effect, not inline: calling a parent's setState during this
  // component's render is what produces React's "cannot update a component
  // while rendering a different component" warning.
  const busyHandler = useRef(onBusyChange);
  busyHandler.current = onBusyChange;
  useEffect(() => {
    busyHandler.current?.(queue.isUploading);
  }, [queue.isUploading]);

  const { enqueue } = queue;
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      if (event.dataTransfer?.files?.length) enqueue(event.dataTransfer.files);
    },
    [enqueue],
  );

  const accept = config?.accepted_mime_types?.join(',') || 'image/*';
  const limitLabel = config
    ? `${config.accepted_extensions.map((ext) => ext.toUpperCase()).join(', ')} · up to ${config.max_upload_mb} MB each`
    : 'Loading accepted formats…';

  return (
    <div className={className}>
      <div
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setIsDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn(
          'rounded-xl border-2 border-dashed text-center transition-colors',
          compact ? 'px-4 py-6' : 'px-6 py-10',
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 bg-gray-50 hover:border-primary-400 dark:border-gray-600 dark:bg-gray-900/40',
        )}
      >
        <ArrowUpTrayIcon
          className={cn(
            'mx-auto text-gray-400 dark:text-gray-500',
            compact ? 'h-7 w-7' : 'h-10 w-10',
          )}
        />
        <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
          Drag images here, or
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="ml-1 text-primary-600 underline-offset-2 hover:underline dark:text-primary-400"
          >
            browse your computer
          </button>
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Pick as many as you like — they upload together. {limitLabel}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="sr-only"
          onChange={(event) => {
            if (event.target.files?.length) queue.enqueue(event.target.files);
            // Reset so re-picking the same file fires `change` again.
            event.target.value = '';
          }}
        />
      </div>

      {queue.items.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {queue.summary.done} of {queue.summary.total} uploaded
              {queue.summary.failed > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {' '}
                  · {queue.summary.failed} failed
                </span>
              )}
            </p>
            <div className="flex gap-2">
              {queue.summary.done > 0 && (
                <Button variant="ghost" size="sm" onClick={queue.clearFinished}>
                  Clear finished
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={queue.clear}>
                Clear all
              </Button>
            </div>
          </div>

          <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {queue.items.map((item) => (
              <UploadRow
                key={item.uid}
                item={item}
                onRetry={() => queue.retry(item.uid)}
                onCancel={() => queue.cancel(item.uid)}
                onRemove={() => queue.remove(item.uid)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UploadRow({
  item,
  onRetry,
  onCancel,
  onRemove,
}: {
  item: UploadItem;
  onRetry: () => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg border p-2',
        item.status === 'error'
          ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
      )}
    >
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-700">
        {item.previewUrl ? (
          <img src={item.previewUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <PhotoIcon className="h-full w-full p-2 text-gray-400" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
          <span className="shrink-0 text-xs text-gray-400">{formatBytes(item.size)}</span>
        </div>

        {item.status === 'uploading' || item.status === 'queued' ? (
          <div className="mt-1.5">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-primary-600 transition-all duration-200"
                style={{ width: `${item.status === 'queued' ? 0 : item.progress}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {item.status === 'queued'
                ? 'Waiting…'
                : item.progress >= 99
                  ? 'Optimising and building sizes…'
                  : `Uploading ${item.progress}%`}
            </p>
          </div>
        ) : item.status === 'done' ? (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
            <CheckCircleIcon className="h-4 w-4" /> Added to the library
          </p>
        ) : (
          <p className="mt-0.5 flex items-start gap-1 text-xs text-red-700 dark:text-red-400">
            <ExclamationCircleIcon className="mt-px h-4 w-4 shrink-0" />
            <span className="break-words">{item.error || 'Upload failed'}</span>
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {(item.status === 'error' || item.status === 'cancelled') && (
          <button
            type="button"
            onClick={onRetry}
            title="Try again"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-700 dark:hover:text-white"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={item.status === 'uploading' ? onCancel : onRemove}
          title={item.status === 'uploading' ? 'Cancel' : 'Remove from list'}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
